import { useQuery } from '@tanstack/react-query'
import type { SharableOpportunityDetailResponse } from 'backend/types'
import { getSharableOpportunityDetail } from '@/lib'
import { queryKeys } from '@/lib/queryKeys'

interface UseSharableOpportunityDetailOptions {
  id: number
  enabled?: boolean // データ取得を有効化するか（デフォルト: true）
}

/**
 * 共有可能案件詳細を取得するカスタムフック
 *
 * @param options - フックオプション（id, enabled）
 * @returns TanStack Query の結果（data, isLoading, error, refetch など）
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSharableOpportunityDetail({
 *   id: 123
 * })
 *
 * if (isLoading) return <LoadingSpinner />
 * if (error) return <div>エラー: {error.message}</div>
 *
 * return <div>{data.customer_issue}</div>
 * ```
 */
export function useSharableOpportunityDetail(options: UseSharableOpportunityDetailOptions) {
  const { id, enabled = true } = options

  return useQuery<SharableOpportunityDetailResponse, Error>({
    queryKey: queryKeys.sharableOpportunityDetail(id),
    queryFn: async () => {
      return getSharableOpportunityDetail(id)
    },
    enabled: enabled && id > 0,
  })
}
